/**
 * Centred reminder overlay that fires when a follow-up's
 * `next_followup_date` arrives.
 *
 * Behaviour:
 *  - Polls /api/contacts/followup-due once a minute (and on tab focus).
 *  - When a new due reminder shows up, plays a short two-tone bell using
 *    the Web Audio API (no audio file needed) and renders a centred
 *    overlay with the lead's details.
 *  - Clicking anywhere — overlay backdrop OR the dismiss button — closes
 *    the popup. Each lead is remembered in localStorage for 24h so the
 *    same reminder doesn't keep firing on every poll.
 */

import React, { useState, useEffect, useRef, useCallback } from 'react';
import ReactDOM from 'react-dom';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Bell, Phone, Mail } from 'lucide-react';

const API_BASE = process.env.REACT_APP_BACKEND_URL;
const POLL_INTERVAL_MS = 60 * 1000;
const DISMISS_KEY = 'followup_reminder_dismissed';
const DISMISS_TTL_MS = 24 * 60 * 60 * 1000;
const SNOOZE_KEY = 'followup_reminder_snoozed';
const SNOOZE_DURATION_MS = 5 * 60 * 1000; // 5-minute snooze

// Module-level so the same context survives re-renders and is reused across
// every reminder firing. Browsers' autoplay policy puts a fresh context in
// the "suspended" state until a user interaction; once we resume it, every
// subsequent tone plays immediately.
let reminderAudioContext = null;

const ensureAudioContext = () => {
  try {
    const Ctx = window.AudioContext || window.webkitAudioContext;
    if (!Ctx) return null;
    if (!reminderAudioContext || reminderAudioContext.state === 'closed') {
      reminderAudioContext = new Ctx();
    }
    if (reminderAudioContext.state === 'suspended' && reminderAudioContext.resume) {
      // Best-effort resume; if the user hasn't interacted yet this is a no-op.
      reminderAudioContext.resume().catch(() => {});
    }
    return reminderAudioContext;
  } catch {
    return null;
  }
};

const playReminderTone = () => {
  try {
    const ctx = ensureAudioContext();
    if (!ctx) return;
    if (ctx.state !== 'running') {
      console.warn('[REMINDER] audio context not running (state =', ctx.state, '); user interaction required first');
      return;
    }
    const now = ctx.currentTime;

    const tone = (freq, start, duration) => {
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.connect(gain);
      gain.connect(ctx.destination);
      osc.type = 'sine';
      osc.frequency.value = freq;
      gain.gain.setValueAtTime(0, now + start);
      gain.gain.linearRampToValueAtTime(0.4, now + start + 0.05);
      gain.gain.linearRampToValueAtTime(0, now + start + duration);
      osc.start(now + start);
      osc.stop(now + start + duration);
    };
    tone(880, 0, 0.25);   // A5
    tone(1320, 0.2, 0.4); // E6
    // Third pulse for a more bell-like decay.
    tone(880, 0.55, 0.35);
  } catch (e) {
    console.warn('[REMINDER] tone failed', e);
  }
};

const readDismissed = () => {
  try {
    const raw = localStorage.getItem(DISMISS_KEY);
    if (!raw) return {};
    const data = JSON.parse(raw);
    const now = Date.now();
    // Drop expired dismissals so old ones don't stick forever.
    return Object.fromEntries(
      Object.entries(data).filter(([, ts]) => now - ts < DISMISS_TTL_MS)
    );
  } catch {
    return {};
  }
};

const writeDismissed = (data) => {
  try {
    localStorage.setItem(DISMISS_KEY, JSON.stringify(data));
  } catch {
    // localStorage may be unavailable (private mode) — fail silently.
  }
};

// Snooze map mirrors the dismissed map but with a per-key timestamp the
// reminder is suppressed UNTIL. Once `now > snoozed_until`, the entry is
// dropped and the reminder re-fires on the next poll.
const readSnoozed = () => {
  try {
    const raw = localStorage.getItem(SNOOZE_KEY);
    if (!raw) return {};
    const data = JSON.parse(raw);
    const now = Date.now();
    return Object.fromEntries(Object.entries(data).filter(([, until]) => until > now));
  } catch {
    return {};
  }
};

const writeSnoozed = (data) => {
  try {
    localStorage.setItem(SNOOZE_KEY, JSON.stringify(data));
  } catch {
    // ignore
  }
};

// Composite key per (contact, scheduled time). When the user reschedules the
// same lead, `next_followup_date` changes and the new key isn't dismissed —
// so the popup correctly fires again instead of being silently swallowed.
const reminderKey = (contact) => `${contact.id}|${contact.next_followup_date || ''}`;

const FollowupReminderOverlay = () => {
  const { token } = useAuth();
  const navigate = useNavigate();
  const [reminder, setReminder] = useState(null); // currently-shown reminder
  const seenIdsRef = useRef(new Set()); // (id|date) keys already alerted on this session

  const fetchDue = useCallback(async () => {
    if (!token) {
      console.log('[REMINDER] no token, skipping poll');
      return;
    }
    try {
      const url = `${API_BASE}/api/contacts/followup-due?token=${token}`;
      const res = await fetch(url);
      console.log('[REMINDER] poll status', res.status);
      if (!res.ok) return;
      const data = await res.json();
      const list = data.contacts || [];
      console.log('[REMINDER] received contacts', list.length, list);
      if (list.length === 0) return;

      const dismissed = readDismissed();
      const snoozed = readSnoozed();
      console.log('[REMINDER] dismissed map', dismissed, 'snoozed map', snoozed);
      // Match by composite key so a fresh schedule on the same lead re-fires.
      // Skip reminders the user has stopped (dismissed) or is currently
      // snoozing — both filters are time-bounded so they auto-expire.
      const next = list.find((c) => {
        const key = reminderKey(c);
        return !seenIdsRef.current.has(key) && !dismissed[key] && !snoozed[key];
      });
      if (!next) {
        console.log('[REMINDER] all due reminders already shown/dismissed this session');
        return;
      }

      console.log('[REMINDER] firing for', next.id, next.name);
      seenIdsRef.current.add(reminderKey(next));
      setReminder(next);
      playReminderTone();
    } catch (err) {
      console.warn('[REMINDER] poll error', err);
    }
  }, [token]);

  useEffect(() => {
    fetchDue();
    const interval = setInterval(fetchDue, POLL_INTERVAL_MS);
    const onFocus = () => fetchDue();
    window.addEventListener('focus', onFocus);

    // Browsers block AudioContext until the user has interacted with the
    // page. Listen for the first click/keydown anywhere and prime the
    // context so the very next reminder can ring without needing the user
    // to interact again.
    const primeAudio = () => {
      const ctx = ensureAudioContext();
      if (ctx && ctx.state === 'running') {
        console.log('[REMINDER] audio context primed');
      }
    };
    document.addEventListener('click', primeAudio, { once: false, capture: true });
    document.addEventListener('keydown', primeAudio, { once: false, capture: true });

    return () => {
      clearInterval(interval);
      window.removeEventListener('focus', onFocus);
      document.removeEventListener('click', primeAudio, { capture: true });
      document.removeEventListener('keydown', primeAudio, { capture: true });
    };
  }, [fetchDue]);

  // (Confirmation step removed — all 4 actions are visible directly so the
  // user can pick the right one without an extra click.)

  // Permanently stop the reminder — clears next_followup_date on the
  // contact via PATCH so the periodic checks won't pick it up again, and
  // keeps the localStorage key as a backstop.
  const stopReminder = useCallback(async () => {
    if (reminder) {
      const dismissed = readDismissed();
      dismissed[reminderKey(reminder)] = Date.now();
      writeDismissed(dismissed);
      try {
        await fetch(`${API_BASE}/api/contacts/${reminder.id}?token=${token}`, {
          method: 'PATCH',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ next_followup_date: null, actionable: false }),
        });
      } catch (e) {
        console.warn('[REMINDER] stop failed', e);
      }
    }
    setReminder(null);
  }, [reminder, token]);

  // Snooze 5 min — backend pushes next_followup_date forward, increments
  // snooze_count, appends history. localStorage acts as a same-tab guard so
  // the popup doesn't immediately re-fire on the next 60s poll before the
  // server-side date update propagates.
  const snoozeReminder = useCallback(async () => {
    if (!reminder) return;
    const oldKey = reminderKey(reminder);
    const snoozed = readSnoozed();
    snoozed[oldKey] = Date.now() + SNOOZE_DURATION_MS;
    writeSnoozed(snoozed);
    seenIdsRef.current.delete(oldKey);
    try {
      await fetch(
        `${API_BASE}/api/contacts/${reminder.id}/snooze-followup?token=${token}&minutes=5`,
        { method: 'POST' }
      );
    } catch (e) {
      console.warn('[REMINDER] snooze failed', e);
    }
    setReminder(null);
  }, [reminder, token]);

  const openLead = useCallback(() => {
    const id = reminder?.id;
    // Treat opening the lead as an explicit acknowledgement so it doesn't
    // re-fire when the user comes back to the dashboard.
    stopReminder();
    if (id) navigate(`/contacts/${id}`);
  }, [reminder, stopReminder, navigate]);

  if (!reminder) return null;

  const formatDue = (iso) => {
    if (!iso) return '';
    try {
      return new Date(iso).toLocaleString();
    } catch {
      return iso;
    }
  };

  // Render via portal into document.body so the overlay always sits on top
  // of every page surface (sidebar, topbar, modals beneath, AI widget, etc.)
  // and naturally disappears the instant `reminder` is cleared — leaving the
  // underlying page interactive again. The backdrop is intentionally light
  // (rgba 0.25) so the user's working page stays clearly visible behind the
  // popup — they shouldn't feel they've left their context to deal with the
  // reminder.
  const overlay = (
    // Dismissal is explicit only — clicking outside the card does NOT close
    // the popup. The user must click Dismiss (acknowledge) or Open Lead.
    // This makes the reminder unmissable; a casual click on the page can't
    // accidentally swallow it.
    <div
      role="dialog"
      aria-modal="true"
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        width: '100vw',
        height: '100vh',
        zIndex: 9999,
        backgroundColor: 'rgba(15, 23, 42, 0.25)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
      }}
    >
      <div
        className="w-full max-w-md mx-4 bg-white dark:bg-slate-900 rounded-2xl shadow-2xl border border-slate-200 dark:border-slate-700 p-6 text-center"
      >
        <div className="mx-auto w-14 h-14 rounded-full bg-purple-100 dark:bg-purple-900/40 flex items-center justify-center mb-4">
          <Bell className="w-7 h-7 text-purple-600 dark:text-purple-400 animate-pulse" />
        </div>
        <h2 className="text-xl font-semibold text-slate-900 dark:text-white mb-1">
          Follow-up reminder
        </h2>
        <p className="text-sm text-slate-500 dark:text-slate-400 mb-5">
          Time to follow up with this lead.
        </p>

        <div className="bg-slate-50 dark:bg-slate-800/60 rounded-xl p-4 text-left space-y-2 mb-5">
          <p className="text-base font-semibold text-slate-900 dark:text-white">
            {reminder.name || 'Unnamed lead'}
          </p>
          {reminder.email && (
            <p className="text-sm text-slate-600 dark:text-slate-300 flex items-center gap-2">
              <Mail className="w-4 h-4" />
              {reminder.email}
            </p>
          )}
          {reminder.phone && (
            <p className="text-sm text-slate-600 dark:text-slate-300 flex items-center gap-2">
              <Phone className="w-4 h-4" />
              {reminder.phone}
            </p>
          )}
          <p className="text-xs text-slate-500 dark:text-slate-400">
            <strong>Scheduled for:</strong> {formatDue(reminder.next_followup_date)}
          </p>
          {reminder.original_followup_date
            && reminder.original_followup_date !== reminder.next_followup_date && (
            <p className="text-xs text-slate-500 dark:text-slate-400">
              <strong>Originally scheduled:</strong> {formatDue(reminder.original_followup_date)}
            </p>
          )}
          {reminder.snooze_count > 0 && (
            <p className="text-xs text-amber-700 dark:text-amber-400">
              <strong>Snoozed:</strong> {reminder.snooze_count} time{reminder.snooze_count === 1 ? '' : 's'}
              {reminder.snoozed_at && ` · last at ${formatDue(reminder.snoozed_at)}`}
            </p>
          )}
          {reminder.last_dismissed_at && (
            <p className="text-xs text-slate-500 dark:text-slate-400">
              <strong>Last dismissed:</strong> {formatDue(reminder.last_dismissed_at)}
            </p>
          )}
          {reminder.followup_notes && (
            <p className="text-sm text-slate-700 dark:text-slate-200 mt-2 whitespace-pre-line">
              {reminder.followup_notes}
            </p>
          )}
        </div>

        <div className="flex gap-2 justify-center flex-wrap">
          <button
            type="button"
            onClick={snoozeReminder}
            title="Push the reminder forward 5 minutes"
            className="px-3 py-2 rounded-lg border border-amber-400 text-amber-700 hover:bg-amber-50 transition text-sm"
          >
            Snooze 5 min
          </button>
          <button
            type="button"
            onClick={stopReminder}
            title="Permanently stop this reminder"
            className="px-3 py-2 rounded-lg bg-red-600 hover:bg-red-500 text-white font-medium transition text-sm"
          >
            Stop reminder
          </button>
          <button
            type="button"
            onClick={openLead}
            className="px-3 py-2 rounded-lg bg-purple-600 hover:bg-purple-500 text-white font-medium transition text-sm"
          >
            Open Lead
          </button>
        </div>

      </div>
    </div>
  );

  return ReactDOM.createPortal(overlay, document.body);
};

export default FollowupReminderOverlay;
